import { useQuery } from '@apollo/client'
import { gql } from '__generated__'
import {
    RosterSearchFilters,
    RosterSearchSortInput,
} from '__generated__/graphql'
import {
    type GridRowParams,
    type GridValidRowModel,
} from '@mui/x-data-grid-pro'
import { createContext, type ReactNode, useEffect, useState } from 'react'
import {
    RosterFilterState,
    RosterSortState,
    toGraphQLRosterFilters,
    useRosterSqlSearchUrlState,
} from 'shared/hooks/rosterSqlSearchUrlState'

export const GetRosterSearchV2 = gql(/* GraphQL */ `
    query GetRosterSearchV2(
        $candidateId: ID
        $enrichmentCandidateId: ID
        $filters: RosterSearchFilters
        $page: Int!
        $limit: Int!
        $sort: [RosterSearchSortInput!]
    ) {
        rosterSearchV2(
            candidateId: $candidateId
            enrichmentCandidateId: $enrichmentCandidateId
            filters: $filters
            page: $page
            limit: $limit
            sort: $sort
        ) {
            pageInfo {
                itemTotal
                pageNumber
                pageTotal
            }
            data {
                id
                name
                company {
                    id
                    name
                }
                createdAt
                createdBy {
                    id
                    firstName
                    lastName
                    nickname
                }
                sharedRecruiters {
                    id
                    firstName
                    nickname
                    lastName
                }
                status
                rosterType
                candidateCount
                rosterCandidateId
                rosterCandidateCreatedAt
                clientFavoritesCount
                internalNote
                processStatusId
                processStatusLabel
                processStatusSummaryGroup
                processStatusUpdatedAt
                sourceStatus
                canEdit
                fundSize
                visaStatuses
                consultants
                sectors
                industries
                locations
                placements {
                    title
                }
            }
        }
    }
`)

export const GetRosterSearchV2AllIds = gql(/* GraphQL */ `
    query GetRosterSearchV2AllIds(
        $candidateId: ID
        $enrichmentCandidateId: ID
        $excludeCandidateId: ID
        $filters: RosterSearchFilters
    ) {
        rosterSearchV2AllIds(
            candidateId: $candidateId
            enrichmentCandidateId: $enrichmentCandidateId
            excludeCandidateId: $excludeCandidateId
            filters: $filters
        )
    }
`)

export interface RosterSqlTableState {
    page: number
    limit: number
    filters: RosterFilterState
    sort: RosterSortState[]
    graphQLSort: RosterSearchSortInput[] | undefined
}

export type RosterRowSelectableFn<
    T extends GridValidRowModel = GridValidRowModel,
> = (params: GridRowParams<T>) => boolean

export interface RosterSqlSearchContextValue {
    tableState: RosterSqlTableState
    setTableState: (partial: Partial<RosterSqlTableState>) => void
    clearFilters: () => void
    hasActiveFilters: boolean
    defaultStatuses: string[]
    data: unknown[]
    isLoading: boolean
    totalItems: number
    totalPages: number
    refetch: () => void
    selectedRosterIds: string[]
    setSelectedRosterIds: (ids: string[]) => void
    selectAllRosters: () => Promise<void>
    isSelectingAll: boolean
    isRowSelectable?: RosterRowSelectableFn
    allSelectableIds: string[] | null
}

export const RosterSqlSearchContext =
    createContext<RosterSqlSearchContextValue | null>(null)

interface RosterSqlSearchProviderProps {
    children: ReactNode
    defaultStatuses: string[]
    candidateId?: string
    enrichmentCandidateId?: string
    excludeCandidateId?: string
    transformFilters?: (filters: RosterFilterState) => RosterSearchFilters
    isRowSelectable?: RosterRowSelectableFn
}

export function RosterSqlSearchProvider({
    children,
    defaultStatuses,
    candidateId,
    enrichmentCandidateId,
    excludeCandidateId,
    transformFilters,
    isRowSelectable,
}: RosterSqlSearchProviderProps) {
    const urlState = useRosterSqlSearchUrlState({ defaultStatuses })
    const [selectedRosterIds, setSelectedRosterIds] = useState<string[]>([])

    const buildFilters = (filters: RosterFilterState) =>
        transformFilters
            ? transformFilters(filters)
            : toGraphQLRosterFilters(filters)

    const filtersKey = JSON.stringify(buildFilters(urlState.filters))
    useEffect(() => {
        setSelectedRosterIds([])
    }, [filtersKey])

    const selectableFilters = {
        ...buildFilters(urlState.filters),
        canEditOnly: true,
    }

    const { data: allIdsData, refetch: refetchAllIds } = useQuery(
        GetRosterSearchV2AllIds,
        {
            variables: {
                candidateId,
                enrichmentCandidateId,
                excludeCandidateId,
                filters: selectableFilters,
            },
        }
    )

    const [isSelectingAll, setIsSelectingAll] = useState(false)

    const selectAllRosters = async () => {
        setIsSelectingAll(true)
        const result = await refetchAllIds()
        if (result.data?.rosterSearchV2AllIds) {
            setSelectedRosterIds(result.data.rosterSearchV2AllIds)
        }
        setIsSelectingAll(false)
    }

    const tableState: RosterSqlTableState = {
        page: urlState.page,
        limit: urlState.limit,
        filters: urlState.filters,
        sort: urlState.sort,
        graphQLSort: urlState.graphQLSort,
    }

    const setTableState = (partial: Partial<RosterSqlTableState>) => {
        if (partial.page !== undefined) urlState.setPage(partial.page)
        if (partial.limit !== undefined) urlState.setLimit(partial.limit)
        if (partial.filters !== undefined) urlState.setFilters(partial.filters)
        if (partial.sort !== undefined) urlState.setSort(partial.sort)
    }

    const {
        data: queryData,
        loading,
        previousData,
        refetch,
    } = useQuery(GetRosterSearchV2, {
        fetchPolicy: 'network-only',
        variables: {
            candidateId,
            enrichmentCandidateId,
            filters: buildFilters(urlState.filters),
            page: tableState.page,
            limit: tableState.limit,
            sort: tableState.graphQLSort,
        },
    })

    const effectiveData = queryData ?? previousData
    const data = effectiveData?.rosterSearchV2?.data ?? []
    const pageInfo = effectiveData?.rosterSearchV2?.pageInfo

    const wrappedSetSelectedRosterIds = (ids: string[]) => {
        let selectableIds = ids

        if (isRowSelectable) {
            selectableIds = selectableIds.filter((id) => {
                const row = data.find((r) => r.id === id)
                return row && isRowSelectable({ row, id, columns: [] })
            })
        }

        setSelectedRosterIds(selectableIds)
    }

    return (
        <RosterSqlSearchContext.Provider
            value={{
                tableState,
                setTableState,
                clearFilters: urlState.clearFilters,
                hasActiveFilters: urlState.hasActiveFilters,
                defaultStatuses,
                data,
                isLoading: loading,
                totalItems: pageInfo?.itemTotal ?? 0,
                totalPages: pageInfo?.pageTotal ?? 0,
                refetch: () => refetch(),
                selectedRosterIds,
                setSelectedRosterIds: wrappedSetSelectedRosterIds,
                selectAllRosters,
                isSelectingAll,
                isRowSelectable,
                allSelectableIds: allIdsData?.rosterSearchV2AllIds ?? null,
            }}
        >
            {children}
        </RosterSqlSearchContext.Provider>
    )
}
